import { Plus, Trash2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { Me } from '@/api/http';
import { useCreateProject, useStudioClients, useStudioTeam } from '@/api/studio';
import { PageHeader } from '@/components/ui';
import { LinearProjectPicker } from './LinearProjectPicker';
import { useStudioTerms, withSign } from '@/lib/contour';
import { everyLabel, everyOptions } from '@/lib/rhythm';
import type { RhythmEvery } from '@/api/types';

type Row = { title: string; extra: string };

function Rows({ rows, onChange, a, b, bType, options }: { rows: Row[]; onChange: (r: Row[]) => void; a: string; b: string; bType?: 'date'; options?: readonly RhythmEvery[] }) {
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-[24px_minmax(0,1fr)_32px] items-center gap-2 sm:grid-cols-[24px_1fr_200px_32px]">
          <span className="num text-right text-sm text-faint">{i + 1}</span>
          <input className="field" placeholder={a} value={r.title} onChange={(e) => onChange(rows.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} />
          {options ? (
            <select className="field max-sm:col-start-2" aria-label={`${b}, строка ${i + 1}`} value={r.extra} onChange={(e) => onChange(rows.map((x, j) => (j === i ? { ...x, extra: e.target.value } : x)))}>
              {options.map((o) => (
                <option key={o} value={o}>
                  {everyLabel[o]}
                </option>
              ))}
            </select>
          ) : (
            <input className="field max-sm:col-start-2" type={bType} placeholder={b} value={r.extra} onChange={(e) => onChange(rows.map((x, j) => (j === i ? { ...x, extra: e.target.value } : x)))} />
          )}
          <button type="button" className="btn-ghost size-8 px-0 max-sm:col-start-3 max-sm:row-span-2 max-sm:row-start-1" aria-label="Убрать" onClick={() => onChange(rows.filter((_, j) => j !== i))}>
            <Trash2 className="size-4" />
          </button>
        </div>
      ))}
      <button type="button" className="btn-ghost" onClick={() => onChange([...rows, { title: '', extra: '' }])}>
        <Plus className="size-4" /> Добавить
      </button>
    </div>
  );
}

export function StudioProjectNew() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { data: clients } = useStudioClients();
  const { data: team } = useStudioTeam();
  const create = useCreateProject();
  const [clientId, setClientId] = useState(params.get('client') ?? '');
  const [kind, setKind] = useState<'fixed' | 'retainer'>('fixed');
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [now, setNow] = useState('Собираем вводные и готовим план работ.');
  const [price, setPrice] = useState('');
  const { currency, sign } = useStudioTerms();
  const me = useQueryClient().getQueryData<Me>(['me']);
  const [ownerId, setOwnerId] = useState('');
  const [teamIds, setTeamIds] = useState<string[]>([]);
  const [finishBy, setFinishBy] = useState('');
  const [linear, setLinear] = useState('');
  const [milestones, setMilestones] = useState<Row[]>([
    { title: 'Бриф', extra: '' },
    { title: 'Прототипы', extra: '' },
    { title: 'Запуск', extra: '' },
  ]);
  const [rhythm, setRhythm] = useState<Row[]>([
    { title: 'Отчёт', extra: 'month' },
    { title: 'Созвон по результатам', extra: '2weeks' },
  ]);

  const client = clientId || clients?.[0]?.id || '';
  // Who leads: the choice, else the signed-in person, else the first of the team.
  const owner = ownerId || team?.find((p) => p.id === me?.person.id)?.id || team?.[0]?.id || '';
  const valid = client && owner && title.trim() && Number(price) > 0;

  return (
    <>
      <PageHeader title="Новый проект" />
      <form
        className="max-w-3xl space-y-8"
        onSubmit={(e) => {
          e.preventDefault();
          if (!valid) return;
          create.mutate(
            {
              clientId: client,
              kind,
              title: title.trim(),
              summary: summary.trim(),
              now: now.trim(),
              price: { amountMinor: Math.round(Number(price) * 100), currency },
              ownerId: owner,
              teamIds: teamIds.filter((id) => id !== owner),
              finishBy: finishBy || undefined,
              milestones: kind === 'fixed' ? milestones.filter((m) => m.title.trim()).map((m) => ({ title: m.title.trim(), due: m.extra || undefined })) : undefined,
              rhythm: kind === 'retainer' ? rhythm.filter((r) => r.title.trim()).map((r) => ({ title: r.title.trim(), every: r.extra as RhythmEvery, source: /отч[её]т/i.test(r.title) ? ('report' as const) : ('manual' as const) })) : undefined,
              linearProject: linear.trim() || undefined,
            },
            { onSuccess: (p) => navigate(`/studio/projects/${p.id}`) },
          );
        }}
      >
        <section className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="mb-1.5 block text-muted">Клиент</span>
            <select className="field" value={client} onChange={(e) => setClientId(e.target.value)}>
              {clients?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <fieldset className="text-sm">
            <legend className="mb-1.5 text-muted">Формат</legend>
            <div className="flex rounded bg-sunken p-0.5">
              {(
                [
                  ['fixed', 'Фиксированная услуга'],
                  ['retainer', 'Регулярные платежи'],
                ] as const
              ).map(([v, l]) => (
                <button key={v} type="button" onClick={() => setKind(v)} className={kind === v ? 'flex-1 rounded-sm bg-canvas py-1.5 font-medium shadow-hair' : 'flex-1 py-1.5 text-muted'}>
                  {l}
                </button>
              ))}
            </div>
          </fieldset>
          <label className="block text-sm sm:col-span-2">
            <span className="mb-1.5 block text-muted">Название, как его увидит клиент</span>
            <input className="field" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Например, «Новый сайт karandashnw.ru»" />
          </label>
          <label className="block text-sm sm:col-span-2">
            <span className="mb-1.5 block text-muted">Зачем проект — одно предложение</span>
            <input className="field" value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="Сайт, который продаёт ремонт под ключ" />
          </label>
          <label className="block text-sm sm:col-span-2">
            <span className="mb-1.5 block text-muted">«Сейчас» — первая фраза для клиента</span>
            <textarea className="field min-h-20" value={now} onChange={(e) => setNow(e.target.value)} />
          </label>
        </section>

        <section>
          <h2 className="mb-2 text-sm font-semibold">{kind === 'fixed' ? 'Этапы' : 'Регулярная работа'}</h2>
          {kind === 'fixed' ? <Rows rows={milestones} onChange={setMilestones} a="Этап" b="Срок" bType="date" /> : <Rows rows={rhythm} onChange={setRhythm} a="Что делаем" b="Как часто" options={everyOptions} />}
        </section>

        <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <label className="block text-sm">
            <span className="mb-1.5 block text-muted">{withSign(kind === 'fixed' ? 'Стоимость проекта' : 'В месяц', sign)}</span>
            <input className="field num" inputMode="numeric" value={price} onChange={(e) => setPrice(e.target.value.replace(/\D/g, ''))} placeholder="65000" />
          </label>
          {kind === 'fixed' && (
            <label className="block text-sm">
              <span className="mb-1.5 block text-muted">Сдача</span>
              <input className="field" type="date" value={finishBy} onChange={(e) => setFinishBy(e.target.value)} />
            </label>
          )}
          <div className="block text-sm">
            <span className="mb-1.5 block text-muted">Проект в Linear</span>
            <LinearProjectPicker value={linear} onChange={setLinear} ariaLabel="Проект в Linear" placeholder="Пусто — «Клиент — Название»" />
          </div>
        </section>

        <section>
          <h2 className="mb-2 text-sm font-semibold">Команда</h2>
          <label className="mb-3 block max-w-xs text-sm">
            <span className="mb-1.5 block text-muted">Ведёт проект</span>
            <select className="field" value={owner} onChange={(e) => setOwnerId(e.target.value)}>
              {team?.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <div className="flex flex-wrap gap-2">
            {team
              ?.filter((p) => p.id !== owner)
              .map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="chip"
                  aria-pressed={teamIds.includes(p.id)}
                  onClick={() => setTeamIds((ids) => (ids.includes(p.id) ? ids.filter((x) => x !== p.id) : [...ids, p.id]))}
                >
                  {p.name}
                </button>
              ))}
          </div>
        </section>

        {create.error && (
          <p role="alert" className="text-sm text-overdue">
            Проект не создан: {create.error.message}
          </p>
        )}

        <div className="flex gap-2 border-t border-line pt-5">
          <button className="btn-primary" disabled={!valid || create.isPending}>
            Создать проект
          </button>
          <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>
            Отмена
          </button>
        </div>
      </form>
    </>
  );
}
